import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  RefreshOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import apiService from '../services/api';
import { AdminLog } from '../types';
import { formatDateTime, toUtcIso } from '../utils/datetime';
import { useAuth } from '../contexts/AuthContext';

// ── Action metadata ──────────────────────────────────────────────────────────

const ACTION_META: Record<string, { label: string; color: 'success' | 'error' | 'info' | 'warning' | 'default' }> = {
  REGISTER_APP:                 { label: 'Register App',           color: 'success' },
  DELETE_APP:                   { label: 'Delete App',             color: 'error' },
  UPDATE_APP:                   { label: 'Update App',             color: 'info' },
  UPDATE_APP_KEYS:              { label: 'Update Keys',            color: 'warning' },
  UPDATE_APP_KEY:               { label: 'Update Key',             color: 'warning' },
  ADD_MODEL:                    { label: 'Add Model',              color: 'success' },
  REVOKE_MODEL:                 { label: 'Revoke Model',           color: 'error' },
  UPDATE_MODEL_LIMITS:          { label: 'Update Limits',          color: 'info' },
  GRANT_ENDPOINT:               { label: 'Grant Endpoint',         color: 'success' },
  REVOKE_ENDPOINT:              { label: 'Revoke Endpoint',        color: 'error' },
  ADD_MODEL_TO_ENDPOINT:        { label: 'Add Model→EP',           color: 'success' },
  REMOVE_MODEL_FROM_ENDPOINT:   { label: 'Remove Model→EP',        color: 'error' },
  UPDATE_ENDPOINT_MODEL_LIMITS: { label: 'EP Model Limits',        color: 'info' },
  ADD_APP_VIEWER:               { label: 'Add Viewer',             color: 'success' },
  REMOVE_APP_VIEWER:            { label: 'Remove Viewer',          color: 'error' },
  CREATE_ADMIN_USER:            { label: 'Create User',            color: 'success' },
  UPDATE_ADMIN_USER:            { label: 'Update User',            color: 'info' },
  DELETE_ADMIN_USER:            { label: 'Delete User',            color: 'error' },
  RESET_TOKEN_USAGE:            { label: 'Reset Tokens',           color: 'warning' },
  RETRY_CREDIT_CHECKS:          { label: 'Retry Credits',          color: 'warning' },
  SYNC_PRICING:                 { label: 'Sync Pricing',           color: 'info' },
  SET_PRICING:                  { label: 'Set Pricing',            color: 'info' },
  DELETE_PRICING:               { label: 'Delete Pricing',         color: 'error' },
  TEST_EMAIL_CONNECTION:        { label: 'Test Email',             color: 'default' },
  SEND_TEST_EMAIL:              { label: 'Send Test Email',        color: 'default' },
  SEND_WEEKLY_REPORT:           { label: 'Weekly Report',          color: 'default' },
  KEYS_APP:                     { label: 'View Keys',              color: 'default' },
  USAGE_APP:                    { label: 'View Usage',             color: 'default' },
  LIFETIME_USAGE:               { label: 'View History',           color: 'default' },
  RETRY_MODEL_CHECK:            { label: 'Retry Model Check',      color: 'info' },
  // Request workflow
  SUBMIT_TOKEN_REQUEST:         { label: 'Request Token Limit',    color: 'warning' },
  SUBMIT_MODEL_REQUEST:         { label: 'Request Model Access',   color: 'warning' },
  APPROVE_REQUEST:              { label: 'Approve Request',        color: 'success' },
  REJECT_REQUEST:               { label: 'Reject Request',         color: 'error' },
  INITIATE_DELETE_APP:          { label: 'Initiate Delete',        color: 'error' },
  CONFIRM_DELETE_APP:           { label: 'Confirm Delete',         color: 'error' },
  CANCEL_DELETE_APP:            { label: 'Cancel Delete',          color: 'warning' },
  // Changelogs
  CHANGELOG_SYNC:               { label: 'Sync Changelog',         color: 'info' },
  CHANGELOG_SEND:               { label: 'Send Changelog',         color: 'success' },
  CHANGELOG_DISCARD:            { label: 'Discard Changelog',      color: 'error' },
  CHANGELOG_SUBSCRIBER_ADD:     { label: 'Add Subscriber',         color: 'success' },
  CHANGELOG_SUBSCRIBER_REMOVE:  { label: 'Remove Subscriber',      color: 'error' },
  // Auth
  ADMIN_LOGIN:                  { label: 'Login',                  color: 'default' },
};

const ROLE_COLORS: Record<string, string> = {
  super_admin: '#FF1A50',
  admin:       '#3B82F6',
  viewer:      '#8B5CF6',
};

const COLOR_MAP = {
  success: { dark: '#00C97A', light: '#059669', bgDark: 'rgba(0,201,122,0.1)', bgLight: '#D1FAE5' },
  error:   { dark: '#FF4D6D', light: '#DC2626', bgDark: 'rgba(255,77,109,0.1)', bgLight: '#FEE2E2' },
  info:    { dark: '#60A5FA', light: '#2563EB', bgDark: 'rgba(96,165,250,0.1)', bgLight: '#DBEAFE' },
  warning: { dark: '#FBBF24', light: '#D97706', bgDark: 'rgba(251,191,36,0.1)', bgLight: '#FEF3C7' },
  default: { dark: '#94A3B8', light: '#64748B', bgDark: 'rgba(148,163,184,0.1)', bgLight: '#F1F5F9' },
};

// ── Sub-components ───────────────────────────────────────────────────────────

const AdminAvatar: React.FC<{ name: string; role: string; isDark: boolean }> = ({ name, role, isDark }) => {
  const initials = name
    ? name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  const color = ROLE_COLORS[role] || '#94A3B8';
  return (
    <Box sx={{
      width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: alpha(color, isDark ? 0.18 : 0.12), color, fontSize: '0.65rem', fontWeight: 700,
      border: `1px solid ${alpha(color, 0.3)}`, flexShrink: 0,
    }}>
      {initials}
    </Box>
  );
};

const ActionChip: React.FC<{ action: string; isDark: boolean }> = ({ action, isDark }) => {
  const meta = ACTION_META[action] || { label: action, color: 'default' as const };
  const c = COLOR_MAP[meta.color];
  return (
    <Box component="span" sx={{
      display: 'inline-block',
      fontSize: '0.72rem', fontWeight: 600,
      px: '7px', py: '2px', borderRadius: '5px',
      bgcolor: isDark ? c.bgDark : c.bgLight,
      color: isDark ? c.dark : c.light,
    }}>
      {meta.label}
    </Box>
  );
};

const DetailsPanel: React.FC<{ details: Record<string, any>; isDark: boolean }> = ({ details, isDark }) => {
  const surface = isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC';
  const border  = isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';
  const textP   = isDark ? '#E2E8F0' : '#1E293B';
  const textS   = isDark ? '#94A3B8' : '#64748B';

  const fmtVal = (v: any): string => {
    if (v === null || v === undefined) return '—';
    if (Array.isArray(v)) return v.length === 0 ? '(none)' : v.join(', ');
    if (typeof v === 'number') return v.toLocaleString();
    if (typeof v === 'boolean') return v ? 'active' : 'inactive';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const PERM_DIFF_KEYS = new Set(['endpoints_added','endpoints_removed','model_changes','limit_changes']);

  if (details.before && details.after) {
    const allFields = Array.from(new Set([...Object.keys(details.before), ...Object.keys(details.after)]));
    const scalarFields = allFields.filter(f => !PERM_DIFF_KEYS.has(f));
    const af = details.after as Record<string, any>;

    // Build human-readable lines for permission/limit changes
    const permLines: string[] = [];
    if (af.endpoints_added?.length)   permLines.push(`Enabled: ${af.endpoints_added.join(', ')}`);
    if (af.endpoints_removed?.length) permLines.push(`Disabled: ${af.endpoints_removed.join(', ')}`);
    if (af.model_changes) {
      Object.entries(af.model_changes).forEach(([ep, mc]: [string, any]) => {
        if (mc.added?.length)   permLines.push(`Added ${mc.added.join(', ')} to ${ep}`);
        if (mc.removed?.length) permLines.push(`Removed ${mc.removed.join(', ')} from ${ep}`);
      });
    }
    if (af.limit_changes) {
      Object.entries(af.limit_changes).forEach(([ep, models]: [string, any]) => {
        Object.entries(models).forEach(([model, diff]: [string, any]) => {
          const b = diff.before || {};
          const a = diff.after  || {};
          permLines.push(
            `${model} on ${ep}: in:${Number(b.input_tokens).toLocaleString()} out:${Number(b.output_tokens).toLocaleString()} → in:${Number(a.input_tokens).toLocaleString()} out:${Number(a.output_tokens).toLocaleString()}`
          );
        });
      });
    }

    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: textS, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
          Changes
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {scalarFields.map(f => {
            const bv = details.before[f];
            const av = details.after[f];
            const changed = JSON.stringify(bv) !== JSON.stringify(av);
            return (
              <Box key={f} sx={{
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                bgcolor: changed ? (isDark ? 'rgba(251,191,36,0.05)' : '#FFFBEB') : surface,
                border: `1px solid ${changed ? (isDark ? 'rgba(251,191,36,0.2)' : '#FDE68A') : border}`,
                borderRadius: '6px', px: 1.5, py: 0.75,
              }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: textS, minWidth: 140 }}>{f.replace(/_/g, ' ')}</Typography>
                <Box sx={{ fontSize: '0.75rem', color: isDark ? '#FCA5A5' : '#B91C1C', textDecoration: 'line-through', opacity: 0.8 }}>{fmtVal(bv)}</Box>
                <Typography sx={{ fontSize: '0.7rem', color: textS }}>→</Typography>
                <Box sx={{ fontSize: '0.75rem', color: isDark ? '#6EE7B7' : '#059669', fontWeight: 500 }}>{fmtVal(av)}</Box>
              </Box>
            );
          })}
          {permLines.map((line, i) => (
            <Box key={`p${i}`} sx={{
              bgcolor: isDark ? 'rgba(251,191,36,0.05)' : '#FFFBEB',
              border: `1px solid ${isDark ? 'rgba(251,191,36,0.2)' : '#FDE68A'}`,
              borderRadius: '6px', px: 1.5, py: 0.75,
            }}>
              <Typography sx={{ fontSize: '0.75rem', color: textP }}>{line}</Typography>
            </Box>
          ))}
        </Box>
        {details.app_name && (
          <Typography sx={{ fontSize: '0.72rem', color: textS, mt: 1.5 }}>App: <strong>{details.app_name}</strong></Typography>
        )}
      </Box>
    );
  }

  const displayKeys = Object.keys(details).filter(k => k !== 'app_name' && details[k] !== undefined && details[k] !== null);
  if (displayKeys.length === 0 && !details.app_name) return null;

  return (
    <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {details.app_name && (
        <Box sx={{ bgcolor: surface, border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 0.75, display: 'flex', gap: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', color: textS }}>App:</Typography>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textP }}>{details.app_name}</Typography>
        </Box>
      )}
      {displayKeys.map(k => (
        <Box key={k} sx={{ bgcolor: surface, border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 0.75, display: 'flex', gap: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', color: textS }}>{k}:</Typography>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textP, wordBreak: 'break-all' }}>
            {Array.isArray(details[k]) ? details[k].join(', ') : typeof details[k] === 'object' ? JSON.stringify(details[k]) : String(details[k])}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const LogRow: React.FC<{ log: AdminLog; isDark: boolean }> = ({ log, isDark }) => {
  const { user } = useAuth();
  const tz = user?.timezone;
  const [open, setOpen] = useState(false);
  const hasDetails = log.details && Object.keys(log.details).length > 0;
  const textP = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS = isDark ? '#888888' : '#666666';
  const roleColor = ROLE_COLORS[log.admin_role] || '#94A3B8';

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasDetails ? 'pointer' : 'default', '& td': { borderBottom: open ? 'none' : undefined } }}
        onClick={() => hasDetails && setOpen(v => !v)}
      >
        <TableCell sx={{ color: textS, fontSize: '0.78rem', whiteSpace: 'nowrap', width: 160 }}>
          <Tooltip title={toUtcIso(log.timestamp)} placement="top">
            <span>{formatDateTime(log.timestamp, tz)}</span>
          </Tooltip>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminAvatar name={log.admin_name} role={log.admin_role} isDark={isDark} />
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: textP, lineHeight: 1.2 }}>
                {log.admin_name || log.admin_email}
              </Typography>
              {log.admin_name && (
                <Typography sx={{ fontSize: '0.7rem', color: textS }}>{log.admin_email}</Typography>
              )}
              {log.admin_role && (
                <Typography sx={{ fontSize: '0.65rem', color: alpha(roleColor, 0.9), fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {log.admin_role.replace('_', ' ')}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>

        <TableCell><ActionChip action={log.action} isDark={isDark} /></TableCell>

        <TableCell sx={{ color: textS, fontSize: '0.8rem' }}>
          {log.resource_name || log.resource || '—'}
        </TableCell>

        <TableCell sx={{ width: 40 }}>
          {hasDetails && (
            <IconButton size="small" sx={{ color: textS }}>
              {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      {hasDetails && (
        <TableRow>
          <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
            <Collapse in={open} unmountOnExit>
              <DetailsPanel details={log.details!} isDark={isDark} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

const ALL_ACTIONS = Object.keys(ACTION_META).sort();

const LogsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [logs, setLogs]       = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);

  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [actionFilter, setAction]   = useState('');
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');

  const textP  = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS  = isDark ? '#888' : '#666';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';

  const loadLogs = useCallback(async (currentPage: number) => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getLogs({
        page: currentPage,
        limit: 50,
        search: search || undefined,
        action: actionFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      setLogs(data.logs || []);
      setTotal(data.total ?? 0);
      setPages(data.pages ?? 1);
    } catch {
      setError('Failed to load logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [search, actionFilter, startDate, endDate]);

  useEffect(() => { setPage(1); }, [search, actionFilter, startDate, endDate]);
  useEffect(() => { loadLogs(page); }, [page, loadLogs]);

  const inputSx = {
    bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: alpha('#FF1A50', 0.4) },
    },
    '& .MuiInputBase-input': { color: textP, fontSize: '0.82rem' },
  };

  const skeletonBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: 'text.primary', letterSpacing: '-0.02em' }}>
            Activity Logs
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.25 }}>
            Full audit trail — who did what, when, and what changed
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              onClick={() => loadLogs(page)}
              disabled={loading}
              sx={{
                color: 'text.secondary',
                border: `1px solid ${border}`,
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                '&:hover': { color: '#FF1A50', borderColor: alpha('#FF1A50', 0.4), bgcolor: alpha('#FF1A50', 0.06) },
              }}
            >
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Filter bar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search admin, action, resource…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: '1 1 220px', ...inputSx }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16, color: textS }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <Select
          size="small"
          displayEmpty
          value={actionFilter}
          onChange={e => setAction(e.target.value)}
          sx={{
            minWidth: 160, fontSize: '0.82rem', color: actionFilter ? textP : textS,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
            '& fieldset': { borderColor: border },
          }}
        >
          <MenuItem value=""><em>All actions</em></MenuItem>
          {ALL_ACTIONS.map(a => (
            <MenuItem key={a} value={a} sx={{ fontSize: '0.82rem' }}>{ACTION_META[a]?.label || a}</MenuItem>
          ))}
        </Select>

        <TextField
          size="small" type="date" label="From"
          value={startDate} onChange={e => setStartDate(e.target.value)}
          sx={{ width: 155, ...inputSx }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small" type="date" label="To"
          value={endDate} onChange={e => setEndDate(e.target.value)}
          sx={{ width: 155, ...inputSx }}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {(search || actionFilter || startDate || endDate) && (
          <Chip
            label="Clear filters"
            size="small"
            onDelete={() => { setSearch(''); setAction(''); setStartDate(''); setEndDate(''); }}
            sx={{ fontSize: '0.75rem', color: textS, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9' }}
          />
        )}
      </Box>

      {/* Count */}
      {!loading && !error && (
        <Typography sx={{ fontSize: '0.78rem', color: textS, mb: 1 }}>
          {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'} found
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${border}` }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: textS } }}>
                <TableCell>Timestamp</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width={140} height={14} sx={{ borderRadius: 1, bgcolor: skeletonBg }} /></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Skeleton variant="circular" width={28} height={28} sx={{ bgcolor: skeletonBg }} />
                        <Box><Skeleton width={120} height={14} sx={{ borderRadius: 1, bgcolor: skeletonBg }} /><Skeleton width={160} height={11} sx={{ borderRadius: 1, bgcolor: skeletonBg, mt: 0.5 }} /></Box>
                      </Box>
                    </TableCell>
                    <TableCell><Skeleton width={90} height={20} sx={{ borderRadius: 1, bgcolor: skeletonBg }} /></TableCell>
                    <TableCell><Skeleton width={120} height={14} sx={{ borderRadius: 1, bgcolor: skeletonBg }} /></TableCell>
                    <TableCell />
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8, color: textS }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>No logs found</Typography>
                      <Typography sx={{ fontSize: '0.8rem' }}>Try adjusting your filters</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => <LogRow key={log._id || idx} log={log} isDark={isDark} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          {loading ? (
            <CircularProgress size={20} sx={{ color: '#FF1A50' }} />
          ) : (
            <Pagination
              count={pages}
              page={page}
              onChange={(_, v) => setPage(v)}
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': { color: textS, borderColor: border },
                '& .Mui-selected': { bgcolor: alpha('#FF1A50', 0.12), color: '#FF1A50', borderColor: alpha('#FF1A50', 0.3) },
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default LogsPage;
